import type { Line } from './lines'

export type SectionId =
  | 'summary'
  | 'experience'
  | 'education'
  | 'skills'
  | 'organization'
  | 'certifications'
  | 'projects'

// Indonesian-first heading lexicon (English variants included). Mirrors the
// section vocabulary used elsewhere in the app (rewrite-section-normalizer.ts).
const HEADING_PATTERNS: Record<SectionId, RegExp[]> = {
  summary: [
    /^ringkasan/i,
    /^tentang saya/i,
    /^profil/i,
    /^(professional |career )?(profile|summary|objective)/i,
    /^about me/i,
  ],
  experience: [
    /^pengalaman( kerja| profesional| magang)?$/i,
    /^riwayat (pekerjaan|kerja)/i,
    /^(work experience|professional experience|experience|employment( history)?)/i,
  ],
  education: [
    /^pendidikan/i,
    /^riwayat pendidikan/i,
    /^(education|academic background)/i,
  ],
  skills: [
    /^keterampilan/i,
    /^keahlian/i,
    /^kemampuan/i,
    /^kompetensi/i,
    /^(skills?|competenc(y|ies)|technical skills)/i,
  ],
  organization: [
    /^organisasi/i,
    /^pengalaman organisasi/i,
    /^kegiatan( organisasi| kemahasiswaan)?/i,
    /^(organizations?|activities|volunteer(ing)?|leadership)/i,
  ],
  certifications: [
    /^sertifik(at|asi)/i,
    /^pelatihan/i,
    /^lisensi/i,
    /^(certifications?|licen[cs]es?|training|courses?)/i,
  ],
  projects: [
    /^proyek/i,
    /^portof?olio/i,
    /^(projects?|portfolio|publications?|awards?)/i,
  ],
}

// A heading is short, has few words, and is not a contact/body line.
export function isLikelyHeading(text: string): boolean {
  const trimmed = text.trim()
  if (!trimmed) return false
  if (trimmed.length > 40) return false
  if (/@|https?:\/\//i.test(trimmed)) return false
  const wordCount = trimmed.split(/\s+/).length
  return wordCount <= 5
}

// Returns the section a line is a heading for, or null if it is not a heading.
export function matchHeading(text: string): SectionId | null {
  if (!isLikelyHeading(text)) return null
  // Strip a trailing colon some CVs add to headings.
  const candidate = text.replace(/[:：]\s*$/, '').trim()

  for (const [section, patterns] of Object.entries(HEADING_PATTERNS) as [
    SectionId,
    RegExp[],
  ][]) {
    if (patterns.some((pattern) => pattern.test(candidate))) {
      return section
    }
  }
  return null
}

export function detectSections(lines: Line[]): Set<SectionId> {
  const found = new Set<SectionId>()
  for (const line of lines) {
    const section = matchHeading(line.text)
    if (section) found.add(section)
  }
  return found
}
